import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { from, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import * as moment from 'moment';
import { PatientListService } from 'src/app/clinical/dashboards/patient-list/patient-list.service';
import { ClinicalTask, ClinicalTaskSource } from 'src/app/models/clients/clinical-task.model';
import { User } from 'src/app/models/user.model';
import { AcuityTaskType } from 'src/app/models/acuity.model';
import { AcuityService } from 'src/app/services/acuity/acuity.service';
import { NewAuthService } from 'src/app/services/auth/new-auth-service.service';
import { DataService } from 'src/app/services/data.service';
import { InitialDataModel } from 'src/app/services/models/initial-data-model.service';
import { ProgramService } from 'src/app/services/programs/program.service';
import { SnackService } from 'src/app/services/snack.service';
import { TaskService } from '../../services/task.service';
import { FirestoreService } from 'src/app/services/firestore.service';
import { UtilsService } from 'src/app/services/utils.service';
import { JourneyJobsService } from 'src/app/services/journey/journey-jobs.service';

const ACUITY_REVIEW_TASK_TYPES: string[] = Object.values(AcuityTaskType);

@Component({
  selector: 'app-task-detail',
  templateUrl: './task-detail.component.html',
  styleUrls: ['./task-detail.component.scss'],
})
export class TaskDetailComponent implements OnInit, OnChanges, OnDestroy {
  @Input() task: any;
  @Output() closeDetail = new EventEmitter<boolean>();
  taskForm: UntypedFormGroup;
  searchModel = { appearance: 'legacy' };
  unsubscribe = new Subject<void>();
  clientsAccount: string[] = [];
  providers: User[] = [];
  priorities = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
  ];
  categories = [];
  lastUpdatedBy = '';
  resolvingAction: 'approve' | 'reject' | null = null;
  isCompletedReview = false;
  apcmTiers: { name: string; value: string }[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private data: DataService,
    private auth: NewAuthService,
    private patientService: PatientListService,
    private taskService: TaskService,
    private snackBarService: SnackService,
    public dataModel: InitialDataModel,
    private programService: ProgramService,
    private utilsService: UtilsService,
    private fs: FirestoreService,
    private acuityService: AcuityService,
    private router: Router,
    private journeyJobsService: JourneyJobsService,
  ) {}

  get isAcuityReviewTask(): boolean {
    return ACUITY_REVIEW_TASK_TYPES.includes(this.task?.type);
  }

  get isApcmTransitionTask(): boolean {
    return this.task?.type === AcuityTaskType.APCM_TRANSITION_REVIEW;
  }

  get isEscalationReviewTask(): boolean {
    return this.task?.type === AcuityTaskType.ESCALATION_REVIEW;
  }

  get isMonthlyJourneyTask(): boolean {
    return this.task?.type === ClinicalTaskSource.MONTHLY_JOURNEY;
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.task.firstChange) {
      this.initializeForm();
    }
  }

  ngOnInit(): void {
    from(this.dataModel.getClientAccounts())
      .pipe(
        switchMap((clients) => {
          this.clientsAccount = clients.map((client) => client.client_account_id);
          return this.taskService.getProviders(this.clientsAccount);
        }),
        takeUntil(this.unsubscribe)
      )
      .subscribe((data) => {
        this.providers = data.sort((a, b) => this.utilsService.sortUsersByAlphabeticLastNameOrder(a, b));
      });
    this.apcmTiers = this.data.apcmTiers;
    this.initializeForm();
    this.getCategories();
    this.lastUpdatedBy = this.task?.last_updated_by ?? '';
  }

  initializeForm(): void {
    this.isCompletedReview = this.isAcuityReviewTask && !!this.task?.completed;
    this.taskForm = this.fb.group({
      patient: ['', Validators.required],
      task_owner: [(this.task && this.task.assignee_id) ?? this.auth.user.user_id, Validators.required],
      due_date: [
        this.task?.due_date && this.task?.due_date?.toDate
          ? this.task?.due_date?.toDate()
          : (this.task?.due_date ?? this.fs.timestamp),
        Validators.required,
      ],
      task_name: [this.task?.task_name ?? '', Validators.required],
      completed: [this.task?.completed ?? false, Validators.required],
      // `task_description` FIRST. Every writer of a clinical task stores `task_description`
      // (`clinical-task.model.ts`) — this dialog, `edit-task-dialog`, and the care call guide's
      // escalation tasks — while this control was the only reader of `description` anywhere in the
      // app. So the field rendered blank here for every task, and for a care call escalation that
      // meant the category, the captured answer, the tier, the protocol clause, the verbatim
      // protocol text and the required action were invisible to the NCM in the clinical task UI;
      // the only surface that showed them was the ADMIN tasks dashboard.
      //
      // `description` is kept as the fallback because tasks saved through this dialog before the fix
      // hold their text there and nowhere else.
      description: [this.task?.task_description ?? this.task?.description ?? ''],
      priority: [this.task?.priority || this.priorities[0].value],
      category: [this.task?.category ?? '', Validators.required],
      other_category: [''],
      reviewNotes: [''],
      selectedApcmTier: ['tier_2'],
      pinned: [false],
    });
    if (this.task) {
      this.getInitialPatient(this.task.patient_id);
      this.taskForm.get('patient').disable();
    }
    if (this.isCompletedReview) {
      this.taskForm.disable();
    }
  }

  getCategories(): void {
    this.programService.getPrograms().subscribe((programs) => {
      const programValues = programs.map((program) =>
        program.value.includes('rpm') ? 'RPM' : program.value.toUpperCase()
      );
      const sortedCategories = [...new Set(programValues), 'AWV'].sort();
      this.categories = [...sortedCategories, 'OTHER'];

      if (this.task?.category) {
        const categoryExists = this.categories.includes(this.task?.category);

        if (!categoryExists) {
          this.taskForm.get('category').setValue('OTHER');
          this.taskForm.get('other_category').setValue(this.task?.category);
        }
      }
    });
  }

  getInitialPatient(id: string): void {
    this.patientService
      .getPatientById(id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((patient) => this.taskForm.get('patient').setValue(patient));
  }

  cancel(): void {
    this.closeDetail.emit(false);
  }

  openCareCallGuide(): void {
    const patientId = this.task?.patient_id;
    if (!patientId) {
      this.snackBarService.genericSnackBar('Patient ID not found', ['error-snackbar'], 4000);
      return;
    }

    this.router.navigate(['/clinical/patient-detail', patientId, 'care-call-guide']);
  }

  saveTask(): void {
    const category = this.taskForm.get('category').value;
    const dueDateControl = this.taskForm.get('due_date')?.value;
    let due_date: Date | null = null;

    if (dueDateControl) {
      const d = moment(dueDateControl);
      d.set('hour', 12);
      due_date = d.toDate();
    }

    const isCreating = !this.task?.id;
    const shouldPin = isCreating && !!this.taskForm.get('pinned')?.value;

    const task = {
      task_name: this.taskForm.get('task_name').value,
      task_name_lower: this.taskForm.get('task_name').value.toLowerCase(),
      patient_id: this.taskForm.get('patient').value.user_id,
      patient_name: this.taskForm.get('patient').value.firstName + ' ' + this.taskForm.get('patient').value.lastName,
      patient_name_lower: (
        this.taskForm.get('patient').value.firstName +
        ' ' +
        this.taskForm.get('patient').value.lastName
      ).toLowerCase(),
      completed: this.taskForm.get('completed').value,
      assignee_id: this.taskForm.get('task_owner').value,
      creator_id: this.auth.user.user_id,
      client_id: this.taskForm.get('patient').value.client_responsible_id,
      client_name: (this.taskForm.get('patient').value.client_responsible ?? '').trim(),
      client_name_lower: (this.taskForm.get('patient').value.client_responsible ?? '').trim().toLowerCase(),
      due_date,
      priority: this.taskForm.get('priority').value,
      // Written back to `task_description`, the field the model declares and every other writer and
      // reader uses. Saving to `description` created a divergent second copy: the dialog edited one
      // field while the admin dashboard, the model and the guide's escalation tasks all used the
      // other. A legacy `description` on an existing document is left untouched rather than cleared
      // — reading prefers `task_description`, so the stale copy is inert, and deleting text a user
      // typed is not this fix's business.
      task_description: this.taskForm.get('description').value,
      category: category === 'OTHER' ? this.taskForm.get('other_category').value : category,
      completed_by:
        this.task?.completed === false && this.taskForm.get('completed').value
          ? this.auth.user.user_id
          : (this.task?.completed_by ?? 'N/A'),
      id: this.task?.id,
      last_updated_by: `${this.auth.user.firstName} ${this.auth.user.lastName}`,
      ...(shouldPin && {
        pinned: true,
        pinnedProperties: {
          pinnedByUserId: this.auth.user.user_id,
          pinnedByFullName: `${this.auth.user.firstName} ${this.auth.user.lastName}`,
        },
      }),
    } as ClinicalTask;
    this.taskService.saveTask(task).subscribe(
      (resp) => {
        this.snackBarService.genericSnackBar('Task saved successfully!', ['success-snackbar'], 5000);
        this.closeDetail.emit(true);
      },
      (error) => {
        // Keep the panel open so the user can retry without losing the form state.
        console.error(error);
        this.snackBarService.genericSnackBar('An error occurred saving the task!', ['error-snackbar'], 5000);
      }
    );
  }

  approveReview(): void {
    this.resolveAcuityReview('approve');
  }

  rejectReview(): void {
    this.resolveAcuityReview('reject');
  }

  private resolveAcuityReview(action: 'approve' | 'reject'): void {
    const patientId = this.task?.patient_id;
    const taskId = this.task?.id;

    if (!patientId || !taskId) {
      this.snackBarService.genericSnackBar('Unable to resolve: missing task information', ['error-snackbar'], 5000);
      return;
    }

    this.resolvingAction = action;

    const onSuccess = (): void => {
      this.resolvingAction = null;
      const message = action === 'approve' ? 'Acuity review approved successfully' : 'Acuity review rejected';
      this.snackBarService.genericSnackBar(message, ['success-snackbar'], 5000);
      if (action === 'approve') {
        this.journeyJobsService.triggerMidMonthUpdate(patientId);
      }
      this.closeDetail.emit(true);
    };

    const onError = (): void => {
      this.resolvingAction = null;
      this.snackBarService.genericSnackBar('Failed to resolve acuity review', ['error-snackbar'], 5000);
    };

    const reviewNotes = this.taskForm.get('reviewNotes')?.value || '';

    if (this.isApcmTransitionTask) {
      const selectedApcmTier = this.taskForm.get('selectedApcmTier')?.value;
      this.acuityService
        .resolveApcmTransitionReview(patientId, {
          action,
          notes: reviewNotes,
          taskId,
          ...(action === 'approve' && { apcmTier: selectedApcmTier }),
        })
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({ next: onSuccess, error: onError });
    } else if (this.isEscalationReviewTask) {
      this.acuityService
        .resolveEscalationReview(patientId, {
          action,
          notes: reviewNotes,
          taskId,
        })
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({ next: onSuccess, error: onError });
    } else {
      this.acuityService
        .resolveDeescalationReview(patientId, {
          action,
          notes: reviewNotes,
          taskId,
        })
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({ next: onSuccess, error: onError });
    }
  }
}
